import React from 'react';
import { Check, X, MapPin, Phone, Building, User, Calendar, Clock, AlertCircle, Wallet, Lock, Folder, Pencil, Archive } from 'lucide-react';
import {
  typeClass, typeHeaderClass, formatDate, daysSince, overdueBadgeClass, overdueLabel,
  inr, netFee, balanceDue, advanceStatusInfo, balanceStatusInfo, isReadyToArchive,
} from './helpers';

const firstName = (n) => (n ? n.split(' ')[0] : '');

function FeeBlock({ r, onSetFee }) {
  if (!r.inspection_done) {
    return <div className="fee-locked"><Lock className="icon" /> Fee after inspection</div>;
  }
  const fee = netFee(r);
  const advance = Number(r.advance || 0);
  if (!fee && !advance) {
    return (
      <div className="fee-set-prompt" onClick={() => onSetFee(r)} data-testid={`fee-set-${r.id}`}>
        <div className="fee-set-prompt-text"><Wallet className="icon" /> Tap to set fee</div>
      </div>
    );
  }
  const bal = balanceDue(r);
  const adv = advanceStatusInfo(r);
  const balInfo = balanceStatusInfo(r);
  return (
    <div className="status-block" onClick={() => onSetFee(r)} data-testid={`fee-block-${r.id}`}>
      <div className="sb-row">
        <div className="sb-left">
          <div className="sb-lbl">Fee</div>
          {Number(r.discount || 0) > 0 && <div className="sb-discount">{'\u2212'}{inr(r.discount)} discount</div>}
        </div>
        <div className="sb-right">
          <div className="sb-amt">{fee > 0 ? inr(r.fees) : '\u2014'}</div>
        </div>
      </div>
      {advance > 0 && (
        <div className="sb-row">
          <div className="sb-left"><div className="sb-lbl">Advance</div><div className="sb-mode">{adv.modeText}</div></div>
          <div className="sb-right"><div className="sb-amt">{inr(advance)}</div><div className={`sb-tag ${adv.tagClass}`}>{adv.tagText}</div></div>
        </div>
      )}
      {fee > 0 && bal > 0 && (
        <div className="sb-row">
          <div className="sb-left"><div className="sb-lbl">Balance</div><div className="sb-mode">{balInfo.modeText}</div></div>
          <div className="sb-right"><div className="sb-amt">{inr(bal)}</div><div className={`sb-tag ${balInfo.tagClass}`}>{balInfo.tagText}</div></div>
        </div>
      )}
      {fee > 0 && bal === 0 && advance >= fee && (
        <div className="sb-row">
          <div className="sb-left"><div className="sb-lbl">Balance</div></div>
          <div className="sb-right"><div className="sb-amt">{inr(0)}</div><div className="sb-tag sb-tag-green">Fully Paid</div></div>
        </div>
      )}
    </div>
  );
}

export default function WorkCard({ work: r, staffById, onToggleInsp, onCycleReport, onOpenDetail, onEditDocsReason, onSetFee, onMoveToClosed }) {
  const st = r.staff_id ? staffById[r.staff_id] : null;
  const inspector = r.inspected_by ? staffById[r.inspected_by] : null;
  const isStability = r.work_type === 'Stability Certificate';
  const days = daysSince(r.created_at);

  let reportBadge = null; let docsNote = null;
  if (r.inspection_done) {
    if (r.collected) reportBadge = <button className="status-badge report-collected" onClick={() => onCycleReport(r)} data-testid={`report-badge-${r.id}`}><Wallet className="icon" /> Report: Collected</button>;
    else if (r.report_done) reportBadge = <button className="status-badge insp-done" onClick={() => onCycleReport(r)} data-testid={`report-badge-${r.id}`}><Check className="icon" /> Report: Completed</button>;
    else if (r.is_on_hold) {
      reportBadge = <button className="status-badge docs-pending" onClick={() => onCycleReport(r)} data-testid={`report-badge-${r.id}`}><Clock className="icon" /> On Hold</button>;
      docsNote = r.hold_reason ? (
        <div style={{ fontSize: 10, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '5px 8px', marginTop: 6, color: '#92400e' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <AlertCircle className="icon" /> <strong>Reason:</strong> {r.hold_reason}
            <button onClick={(e) => { e.stopPropagation(); onEditDocsReason(r); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#92400e', cursor: 'pointer', padding: 0 }}><Pencil className="icon" /></button>
          </div>
          {r.hold_notes && (
            <div style={{ marginTop: 3, paddingLeft: 14, color: '#78350f', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{r.hold_notes}</div>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 6 }}>
          <button onClick={() => onEditDocsReason(r)} style={{ background: 'none', border: 'none', color: '#92400e', cursor: 'pointer', padding: 0, fontSize: 10, textDecoration: 'underline' }} data-testid={`add-reason-${r.id}`}>+ Add reason</button>
        </div>
      );
    } else reportBadge = <button className="status-badge insp-pending" onClick={() => onCycleReport(r)} data-testid={`report-badge-${r.id}`}><X className="icon" /> Report: Not Completed</button>;
  }

  const ctClass = r.contact_type ? 'ct-' + r.contact_type.toLowerCase().replace(/\s+/g, '-') : '';

  return (
    <div className={`report-card ${typeClass(r.work_type)}`} data-testid={`work-card-${r.id}`}>
      <div className={`card-type-header ${typeHeaderClass(r.work_type)}`}>
        <span className="card-type-label">{r.work_type}</span>
        <span className="card-office-label">{r.office} Office</span>
      </div>
      <div className="report-top">
        <div className="report-info">
          <div className={`report-name ${r.client_name === 'Name not added' || !r.client_name ? 'placeholder-value' : ''}`}>{r.client_name || 'Name not added'}</div>
          <div className="meta-line"><MapPin className="icon" /> {r.location ? r.location : <span className="placeholder-value">Location not added</span>}</div>
          {r.mobile && (
            <div className="meta-line"><Phone className="icon" />
              <a href={`tel:${r.mobile}`} style={{ color: '#1c1917', textDecoration: 'none', fontWeight: 600 }}>{r.mobile}</a>
              {r.contact_type && <span className={`contact-type-badge ${ctClass}`}>{r.contact_type}</span>}
            </div>
          )}
          {!isStability && <div className="meta-line"><Building className="icon" /> <span className={!r.bank || r.bank === 'Bank not added' ? 'placeholder-value' : ''}>{r.bank || 'Bank not added'}</span>{r.branch ? ' \u2014 ' + r.branch : ''}</div>}
          <div className="status-bar">
            {r.inspection_done
              ? <button className="status-badge insp-done" onClick={() => onToggleInsp(r)} data-testid={`insp-badge-${r.id}`}><Check className="icon" /> Inspection: Completed{inspector ? ' \u00b7 by ' + firstName(inspector.name) : ''}</button>
              : <button className="status-badge insp-pending" onClick={() => onToggleInsp(r)} data-testid={`insp-badge-${r.id}`}><X className="icon" /> Inspection: Not Completed</button>}
            {reportBadge}
            {!r.inspection_done && <span className={`overdue-badge ${overdueBadgeClass(days)}`}><Clock className="icon" /> {overdueLabel(days)}</span>}
          </div>
          {docsNote}
        </div>
        <div className="report-right">
          <FeeBlock r={r} onSetFee={onSetFee} />
          {isReadyToArchive(r) && <button className="move-completed-btn" onClick={() => onMoveToClosed(r)} data-testid={`move-closed-${r.id}`}><Archive className="icon" /> Move to Closed</button>}
          <button className="btn-open-detail" onClick={() => onOpenDetail(r)} data-testid={`open-detail-${r.id}`}><Folder className="icon" /> Open</button>
        </div>
      </div>
      <div className={`card-type-footer ${typeHeaderClass(r.work_type).replace('-header', '-footer')}`}>
        <span className="card-footer-staff"><User className="icon" /> Staff: <strong>{st ? st.name : '\u2014'}</strong></span>
        <span className="card-footer-date"><Calendar className="icon" /> {formatDate(r.created_at)}</span>
      </div>
    </div>
  );
}
